import type { BadgeTone } from '@/components/base/Badge';
import type { RoleRecord, StaffMember } from '@/types/portal';

export interface StaffTotals {
  total: number;
  active: number;
  invited: number;
  deactivated: number;
  rolesInUse: number;
  twoFactorCount: number;
}

export function staffTotals(rows: StaffMember[]): StaffTotals {
  const roles = new Set(rows.map((member) => member.roleId));
  return {
    total: rows.length,
    active: rows.filter((member) => member.status === 'Active').length,
    invited: rows.filter((member) => member.status === 'Invited').length,
    deactivated: rows.filter((member) => member.status === 'Deactivated').length,
    rolesInUse: roles.size,
    twoFactorCount: rows.filter((member) => member.twoFactor).length,
  };
}

export interface StaffFilter {
  status: string;
  roleId: string;
  markets: string[];
  query: string;
}

export function filterStaff(rows: StaffMember[], filter: StaffFilter): StaffMember[] {
  const text = filter.query.trim().toLowerCase();
  return rows.filter((member) => {
    if (filter.status !== 'All' && member.status !== filter.status) return false;
    if (filter.roleId !== 'All roles' && member.roleId !== filter.roleId) return false;
    if (
      filter.markets.length > 0 &&
      !member.markets.some((market) => filter.markets.includes(market))
    ) {
      return false;
    }
    if (text) {
      const haystack = `${member.name} ${member.email} ${member.roleName} ${member.markets.join(' ')}`.toLowerCase();
      if (!haystack.includes(text)) return false;
    }
    return true;
  });
}

const ROLE_TONES: Record<string, BadgeTone> = {
  admin: 'primary',
  finance: 'accent',
  creator_manager: 'secondary',
  viewer: 'neutral',
  client: 'outline',
};

export function roleTone(roleId: string): BadgeTone {
  return ROLE_TONES[roleId] ?? 'neutral';
}

export interface RoleHeadcount {
  role: RoleRecord;
  count: number;
}

/** Headcount per role, in the order the roles are declared. */
export function roleHeadcount(roles: RoleRecord[], members: StaffMember[]): RoleHeadcount[] {
  return roles.map((role) => ({
    role,
    count: members.filter((member) => member.roleId === role.id && member.status !== 'Deactivated').length,
  }));
}

/** Does the signed-in user only see themselves as the administrator? */
export function isLastAdmin(members: StaffMember[], targetId: string): boolean {
  const admins = members.filter((member) => member.roleId === 'admin' && member.status === 'Active');
  return admins.length === 1 && admins[0].id === targetId;
}
